import {
  Card,
  CardNumberSuccess,
  CardStatusEnum,
  CardTypeEnum,
} from '@varo/graphql-schema-types';
import { setStringAsync } from 'expo-clipboard';
import { useFrozenCardEnabled } from 'features/cardManagement/hooks/useCardLockExperience/useCardLockExperience';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { CardFront } from 'shared/components/vxl/DigitalCard/CardFront';
import CardFrozenVisual from 'shared/components/vxl/DigitalCard/CardFrozenVisual';
import DigitalCard from 'shared/components/vxl/DigitalCard/DigitalCard';
import { isIOS, isWeb } from 'shared/constants/platform';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { GlobalUIContext } from 'shared/providers/globalUIProvider/GlobalUIContextProvider/GlobalUIContextProvider';
import theme from 'shared/styles/theme';
import { toTitleCase } from 'shared/utils/string/string.helpers';

export interface CardComponentProps {
  card: Card;
  cvvData?: CardNumberSuccess;
  isExpired: boolean;
  firstName: string;
  lastName: string;
  isLoading: boolean;
  getCvvDetails: () => void;
}

const CardComponent = ({
  card,
  cvvData,
  isExpired,
  firstName,
  lastName,
  isLoading,
  getCvvDetails,
}: CardComponentProps) => {
  const { t: translatedText } = useTranslation('cardManagement', {
    keyPrefix: 'manageCard',
  });

  const isFrozenLanguageEnabled = useFrozenCardEnabled();

  const { setSnackBarMessage } = useContext(GlobalUIContext);

  const handleCopyCardNumberButtonAnalytics = usePressableAnalytics({
    analyticsId: 'copyCardNumber',
    customAttributes: {
      card_type: card.type?.toLowerCase(),
    },
  });

  const handleCopyToClipboard = () => {
    handleCopyCardNumberButtonAnalytics();
    void setStringAsync((cvvData?.pan ?? '')?.replace(/\s/g, ''));
    setSnackBarMessage?.(translatedText('numberCopied'));
  };

  const { isLocked } = card;
  const showFrozenIcon = isLocked && isFrozenLanguageEnabled;
  const mutedCard = isLocked || card.status === CardStatusEnum.Suspended;

  return !isExpired &&
    (card.status === CardStatusEnum.New ||
      card.status === CardStatusEnum.Suspended ||
      isWeb) ? (
    <View style={styles.cardContainer}>
      <View style={mutedCard ? styles.mutedAlpha : null}>
        <CardFront
          cardType={card.type}
          firstNameText={toTitleCase(firstName)}
          lastNameText={toTitleCase(lastName)}
        />
      </View>
      {showFrozenIcon ? (
        <View style={styles.frozenIconStyle}>
          <CardFrozenVisual iconColor="white" iconSize="MD" />
        </View>
      ) : null}
    </View>
  ) : (
    <DigitalCard
      firstNameText={toTitleCase(firstName)}
      lastNameText={toTitleCase(lastName)}
      cardNumberText={cvvData?.pan ?? ''}
      expirationLabel={translatedText('expiration')}
      expirationDateText={cvvData?.expiration ?? ''}
      cvvLabel={translatedText('cvv')}
      cvvNumberText={cvvData?.cvv2 ?? ''}
      cardType={card.type as CardTypeEnum}
      canGetInfo
      isSuspended={false}
      showCardInfo={false}
      isLocked={isLocked}
      isExpired={isExpired}
      button={{
        onPress: () => {
          if (!cvvData) {
            void getCvvDetails();
          }
        },
      }}
      handleCopyToClipboard={handleCopyToClipboard}
      cvvFetching={isLoading}
      isFrozenLanguageEnabled={isFrozenLanguageEnabled}
    />
  );
};

const styles = StyleSheet.create({
  bottomSheet: {
    paddingHorizontal: theme.spacing.m,
  },
  bottomSheetContainer: {
    alignItems: 'center',
    backgroundColor: theme.color.background.primary,
    gap: theme.spacing.xs,
    padding: theme.spacing.m,
  },
  bottomSheetSubTitle: {
    paddingVertical: theme.spacing.s,
  },
  cardContainer: {
    alignItems: 'center',
    bottom: 0,
    height: 343,
    justifyContent: 'center',
    left: 0,
    margin: theme.spacing.m,
    marginLeft: 'auto',
    marginRight: 'auto',
    position: 'relative',
    right: 0,
    top: 0,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  content: { padding: theme.spacing.m },
  contentView: { paddingVertical: theme.spacing.m },
  frozenIconStyle: {
    alignItems: 'center',
    bottom: 0,
    justifyContent: 'center',
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
  },
  hairLine: {
    marginHorizontal: theme.spacing.m,
  },
  label: { color: theme.color.text.secondary },
  mutedAlpha: {
    opacity: 0.6,
  },
  walletProvisioningButton: {
    height: 40,
    margin: isIOS ? theme.spacing.m : theme.spacing.s,
  },
});

export default CardComponent;
